import type { CustomSurveyResponse } from '../../api/customSurveys';
import type { CustomSurveyTemplate } from '../../api/customSurveyTemplates';

/** Den siste undersøkelsen fra en mal: den som pågår, ellers den nyeste */
export type LatestSurvey = {
  id: number;
  uuid: string;
  isActive: boolean;
  responseRate: number;
  responseCount: number;
  expectedCount: number;
  /** Frist som ISO-dato, når undersøkelsen har en frist */
  deadline?: string;
  /** Datoen historikken sorteres og grupperes etter: fristen, ellers avslutning eller utsendelse */
  date?: string;
};

export type TemplateItem = {
  template: CustomSurveyTemplate;
  latest?: LatestSurvey;
};

export type MonthGroup = {
  /** ÅÅÅÅ-MM, eller null når målingen mangler dato */
  key: string | null;
  items: TemplateItem[];
};

const DAY_MS = 24 * 60 * 60 * 1000;

const deadlineOf = (survey: CustomSurveyResponse) => {
  if (!survey.activatedAt || !survey.deadlineInDays) return undefined;
  return new Date(new Date(survey.activatedAt).getTime() + survey.deadlineInDays * DAY_MS).toISOString();
};

const toLatest = (survey: CustomSurveyResponse): LatestSurvey => {
  const deadline = deadlineOf(survey);
  return {
    id: survey.id,
    uuid: survey.uuid,
    isActive: survey.isActive,
    responseRate: survey.responseRate ?? 0,
    responseCount: survey.responseCount ?? 0,
    expectedCount: survey.expectedParticipantsCount,
    deadline,
    date: deadline ?? survey.completedAt ?? survey.activatedAt ?? undefined,
  };
};

const sentFrom = (templateId: number, surveys: CustomSurveyResponse[]) =>
  surveys.filter((survey) => survey.templateId === templateId && survey.activatedAt);

export const latestSurvey = (templateId: number, surveys: CustomSurveyResponse[]): LatestSurvey | undefined => {
  const newestFirst = sentFrom(templateId, surveys).sort(
    (a, b) => new Date(b.activatedAt!).getTime() - new Date(a.activatedAt!).getTime(),
  );
  const survey = newestFirst.find((item) => item.isActive) ?? newestFirst[0];
  return survey ? toLatest(survey) : undefined;
};

/**
 * Historikken: én rad per mal med den siste fullførte undersøkelsen, også når en ny pågår.
 * Inaktiv er ikke det samme som fullført: når fristen går ut, er undersøkelsen inaktiv
 * uten completedAt til den er forlenget eller lukket. Den vises derfor ikke her ennå.
 * Maler som aldri er sendt ut, vises uten dato.
 */
export const historyItems = (templates: CustomSurveyTemplate[], surveys: CustomSurveyResponse[]): TemplateItem[] =>
  templates.flatMap((template) => {
    const sent = sentFrom(template.id, surveys);
    if (sent.length === 0) return [{ template }];
    const completed = sent
      .filter((survey) => survey.completedAt)
      .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime());
    return completed.length > 0 ? [{ template, latest: toLatest(completed[0]) }] : [];
  });

/** Malene med en pågående undersøkelse, med nærmeste frist først */
export const liveItems = (items: TemplateItem[]) =>
  items
    .filter((item) => item.latest?.isActive)
    .sort((a, b) => (a.latest?.deadline ?? '').localeCompare(b.latest?.deadline ?? ''));

/** Hele dager fra i dag til fristen. 0 betyr siste dag i dag. */
export const daysLeft = (deadline: string, today = new Date()) => {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const end = new Date(deadline);
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return Math.max(0, Math.round((endDay - start) / DAY_MS));
};

export const categoriesOf = (items: TemplateItem[]) =>
  [...new Set(items.map((item) => item.template.category).filter((category): category is string => Boolean(category)))].sort(
    (a, b) => a.localeCompare(b, 'nb'),
  );

/** ÅÅÅÅ-MM i lokal tid, så en frist ved midnatt havner i riktig måned */
const monthKey = (date: string) => {
  const value = new Date(date);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
};

/** Historikken som passer søket og kategorien, gruppert per måned med nyeste først */
export const historyByMonth = (items: TemplateItem[], query: string, category: string): MonthGroup[] => {
  const search = query.trim().toLowerCase();
  const done = items
    .filter((item) => !search || item.template.name.toLowerCase().includes(search))
    .filter((item) => !category || item.template.category === category)
    .sort(
      (a, b) =>
        (b.latest?.date ?? '').localeCompare(a.latest?.date ?? '') || a.template.name.localeCompare(b.template.name, 'nb'),
    );

  const groups = new Map<string | null, TemplateItem[]>();
  done.forEach((item) => {
    const key = item.latest?.date ? monthKey(item.latest.date) : null;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => Number(a === null) - Number(b === null))
    .map(([key, grouped]) => ({ key, items: grouped }));
};
